import type { BodyHandle, ShapeHandle } from 'box3d-web';
import {
  createHostedSiegeRuntime,
  disposeSiegeStructure,
  finalizeSiegeHostedFrame,
  advanceSiegeHostedFixedStep,
  resetSiege,
  spawnCannonball,
  type SiegeContactBatch,
  type SiegeFanout,
  type SiegeNativeOwnership,
  type SiegeRuntime,
  SIEGE_FIXED_DT,
  SIEGE_SUBSTEPS,
} from '../../structures/siege/siegePhysics';
import type { SceneCommandEnvelope, SceneContactBatch, DestructionSceneParticipant } from '../destructionSceneParticipant';
import { createDestructionIdentityFactory, type StructureIdentity } from '../identities';
import { SharedDestructionWorld, type DestructionStructureHost } from '../sharedDestructionWorld';

export interface SiegeFireCommand {
  readonly origin: { readonly x: number; readonly y: number; readonly z: number };
  readonly direction: { readonly x: number; readonly y: number; readonly z: number };
  readonly charge: number;
}

export interface SiegeStructureAdapterOptions {
  readonly shared: SharedDestructionWorld;
  readonly structureId: StructureIdentity;
  readonly groundBody: BodyHandle;
}

function vector(value: unknown, label: string, nonZero = false): { x: number; y: number; z: number } {
  if (!value || typeof value !== 'object') throw new RangeError(`${label} is invalid`);
  const v = value as { x?: unknown; y?: unknown; z?: unknown };
  if (![v.x, v.y, v.z].every((n) => typeof n === 'number' && Number.isFinite(n)) || (nonZero && Math.hypot(v.x as number, v.y as number, v.z as number) < 1e-8)) {
    throw new RangeError(`${label} must contain finite coordinates`);
  }
  return { x: v.x as number, y: v.y as number, z: v.z as number };
}

/** Siege's wall/projectiles hosted in a caller-owned shared world. */
export class HostedSiegeStructureAdapter implements DestructionSceneParticipant<SiegeFanout> {
  private disposed = false;
  private sceneOwner: object | null = null;
  private readonly host: DestructionStructureHost;
  private readonly projectileBodies: Set<number>;
  readonly runtime: SiegeRuntime;

  private constructor(runtime: SiegeRuntime, host: DestructionStructureHost, projectileBodies: Set<number>) {
    this.runtime = runtime;
    this.host = host;
    this.projectileBodies = projectileBodies;
  }

  static async create(options: SiegeStructureAdapterOptions): Promise<HostedSiegeStructureAdapter> {
    const host = options.shared.createStructureHost(options.structureId);
    const ids = createDestructionIdentityFactory({ namespace: options.structureId.namespace, generation: options.structureId.generation });
    const ownership: SiegeNativeOwnership = {
      registerBody: (body, participant) => {
        if (participant.kind === 'projectile') {
          const ordinal = options.shared.allocateWorldActorOrdinal('projectile');
          options.shared.registerWorldBody(body, { scope: 'world', worldId: options.shared.worldId, actor: 'projectile', ordinal });
          projectileBodies.add(body as number);
        } else {
          const sourceOrdinal = participant.kind === 'fragment' ? participant.sourceBrickOrdinal : participant.ordinal;
          const element = ids.element(sourceOrdinal, options.structureId);
          const layer = ids.layer(sourceOrdinal, element);
          const cell = ids.cell(sourceOrdinal, layer);
          const address = {
            scope: 'structure' as const,
            worldId: options.shared.worldId,
            structureId: options.structureId,
            elementId: element,
            layerId: layer,
            cellId: cell,
            ...(participant.kind === 'fragment' ? { fragmentId: ids.fragment(participant.ordinal, cell) } : {}),
          };
          host.registerBody(body, address);
        }
      },
      registerShape: (shape: ShapeHandle, body) => {
        if (projectileBodies.has(body as number)) options.shared.registerWorldShape(shape, body);
        else host.registerShape(shape, body);
      },
      unregisterBody: (body) => {
        if (projectileBodies.delete(body as number)) options.shared.unregisterWorldBody(body);
        else host.unregisterBody(body);
      },
    };
    const projectileBodies = new Set<number>();
    try {
      const runtime = await createHostedSiegeRuntime({ world: options.shared.world, groundBody: options.groundBody, ownership });
      return new HostedSiegeStructureAdapter(runtime, host, projectileBodies);
    } catch (error) {
      host.dispose();
      throw error;
    }
  }

  get structureKey(): string { return this.host.structureId.key; }
  get revision(): number { return this.host.revision; }
  get physicsWorld() { return this.runtime.world; }
  get fixedDt(): number { return SIEGE_FIXED_DT; }
  get substeps(): number { return SIEGE_SUBSTEPS; }

  sceneCommandPolicy(type: string): 'critical' | 'coalescible' { return type === 'set-siege-charge' ? 'coalescible' : 'critical'; }

  prepareSceneCommand(type: string, payload: unknown): unknown {
    if (type !== 'siege-fire') return payload;
    const p = payload as Partial<SiegeFireCommand>;
    const origin = vector(p.origin, 'siege-fire.origin');
    const direction = vector(p.direction, 'siege-fire.direction', true);
    if (typeof p.charge !== 'number' || !Number.isFinite(p.charge) || p.charge < 0 || p.charge > 1) throw new RangeError('siege-fire.charge must be between 0 and 1');
    return Object.freeze({ origin: Object.freeze(origin), direction: Object.freeze(direction), charge: p.charge });
  }

  applySceneCommand(owner: object, command: SceneCommandEnvelope): void {
    this.assertOwner(owner);
    if (command.type === 'siege-fire') {
      const p = command.payload as SiegeFireCommand;
      spawnCannonball(this.runtime, p.origin, p.direction, p.charge);
    } else {
      throw new Error(`unsupported Siege command ${command.type}`);
    }
  }

  sceneFixedStep(owner: object, batch: SceneContactBatch): void {
    this.assertOwner(owner);
    const siegeBatch: SiegeContactBatch = { begins: batch.begins, ends: batch.ends, hits: batch.hits };
    advanceSiegeHostedFixedStep(this.runtime, siegeBatch);
  }

  finishSceneFrame(owner: object, delta: number, stepped: boolean): void { this.assertOwner(owner); void delta; void stepped; }
  drainSceneEvents(owner: object): readonly SiegeFanout[] { this.assertOwner(owner); return finalizeSiegeHostedFrame(this.runtime); }
  sceneReset(owner: object): void { this.assertOwner(owner); resetSiege(this.runtime); this.host.advanceRevision(); }
  sceneResetClock(owner: object): void { this.assertOwner(owner); this.runtime.stepper.reset(); this.runtime.simTime = 0; }
  ownsProjectileBody(body: BodyHandle): boolean { return !this.disposed && this.projectileBodies.has(body as number); }
  attachSceneOwner(owner: object): void { if (this.sceneOwner && this.sceneOwner !== owner) throw new Error('Siege adapter is already attached'); this.sceneOwner = owner; }
  detachSceneOwner(owner: object): void { if (this.sceneOwner === owner) this.sceneOwner = null; }
  dispose(): void {
    if (this.disposed) return;
    if (this.sceneOwner) throw new Error('Siege adapter is scene-owned; detach first');
    try { disposeSiegeStructure(this.runtime); } finally { try { this.host.dispose(); } finally { this.disposed = true; } }
  }

  private assertOwner(owner: object): void { if (this.disposed) throw new Error('Siege adapter used after dispose()'); if (this.sceneOwner !== owner) throw new Error('Siege adapter is not attached'); }
}

export async function createSiegeStructureAdapter(options: SiegeStructureAdapterOptions): Promise<HostedSiegeStructureAdapter> {
  return HostedSiegeStructureAdapter.create(options);
}
